import { computed, toValue, type MaybeRefOrGetter } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import { getAssetDetailsForCreditNote, getCustomerForCreditNote, getAssetVat } from '@/api/billing/credit-notes'
import type { GetAssetDetailsResponse, CreditNoteCustomer } from '@/api/billing/credit-note-schemas'

export type CreditNoteDraftSeed = {
  asset: GetAssetDetailsResponse
  customer: CreditNoteCustomer
  vatPercentage: number
}

export function useCreditNoteDraftSeed(assetCode: MaybeRefOrGetter<string>) {
  const assetCodeClean = computed(() => String(toValue(assetCode) ?? '').trim())
  const enabledAsset = computed(() => assetCodeClean.value.length > 0)

  const assetQuery = useQuery({
    queryKey: computed(() => ['credit-note-draft-asset', assetCodeClean.value]),
    enabled: enabledAsset,
    queryFn: () => getAssetDetailsForCreditNote(assetCodeClean.value)
  })

  const enabledCustomer = computed(() => !!assetQuery.data.value)
  const customerQuery = useQuery({
    queryKey: computed(() => {
      const a = assetQuery.data.value
      return [
        'credit-note-draft-customer',
        a?.customerExternalIdentifier ?? null,
        a?.contract?.contractFundEntity?.id ?? null
      ]
    }),
    enabled: enabledCustomer,
    queryFn: () => {
      const a = assetQuery.data.value!
      return getCustomerForCreditNote({
        customerExternalIdentifier: a.customerExternalIdentifier,
        contractFundEntityId: a.contract.contractFundEntity.id
      })
    }
  })

  const vatQuery = useQuery({
    queryKey: computed(() => ['credit-note-draft-vat', assetCodeClean.value]),
    enabled: enabledAsset,
    queryFn: () => getAssetVat(assetCodeClean.value)
  })

  const vatReady = computed(() => vatQuery.isFetched.value)

const seed = computed<CreditNoteDraftSeed | null>(() => {
  const asset = assetQuery.data.value
  const customer = customerQuery.data.value
  if (!asset || !customer || !vatReady.value) return null

  const vatPercentage = vatQuery.data.value?.ratePercentage ?? 0
  return { asset, customer, vatPercentage }
})

  const isLoading = computed(
    () => assetQuery.isFetching.value || customerQuery.isFetching.value || vatQuery.isFetching.value
  )

  const isError = computed(() => assetQuery.isError.value || customerQuery.isError.value || vatQuery.isError.value)

  const error = computed(() => assetQuery.error.value ?? customerQuery.error.value ?? vatQuery.error.value)

  return { seed, isLoading, isError, error, assetQuery, customerQuery, vatQuery }
}
